import { DataTag, QueryKey, useQueryClient } from '@mint-lib/query-client';
import { QueryClient, SetDataOptions, Updater } from '@tanstack/query-core';

import type {
  AsyncUploadState,
  AsyncValidationState,
  TemplateCollaborator,
  ValidationSearchParams,
} from '../api/types.js';
import { DeferredPromise } from '../utils/promise.js';
import { removeFalsyQueryKeys } from '../utils/rq.js';

export { initialSearchParams } from './../constants/tablesConfig.js';

const queryKeysFabrics = {
  resources: {
    base: () => ['resources'],
    exampleCsvFile: () => ['resources', 'exampleCsvFile'] as const,
    campaignsList: ['resources', 'campaignsList'],
    platformsByCampaign: (campaignID: number) => [
      'resources',
      'platformsList',
      campaignID,
    ],
    platformsByNameAndLabel: ['resources', 'platformsByNameAndLabel'],
    allPlatformsList: (filters: {
      campaignCollectionId?: null | number;
      channelName?: null | string;
    }) => ['resources', 'allPlatformsList', filters],
    channelNamesList: (filters: {
      campaignCollectionId?: null | number;
      platformName?: null | string;
      tileId?: null | string;
    }) => ['resources', 'channelNamesList', filters],
    mediaRowsById: (filters: { campaignCollectionId: number }) => [
      'resources',
      'mediaRowsById',
      filters,
    ],
  },
  dataFiles: {
    totalFilesToArchiveCount: () =>
      ['resources', 'totalFilesToArchiveCount'] as const,
  },
  template: {
    preview(templateId: string) {
      return [templateId, 'preview'];
    },
    collaborators: (templateId: string) =>
      [templateId, 'collaborators'] as const,
  },
  dataFile: {
    base(dataFileId: string) {
      return [dataFileId];
    },
    steps: {
      base(dataFileId: string) {
        return [dataFileId, 'steps'];
      },
      upload: {
        base(dataFileId: string) {
          return [dataFileId, 'steps', 'upload'] as const;
        },
        awsUploadData(dataFileId: string) {
          return [...this.base(dataFileId), 'awsUploadData'] as const;
        },
        state(dataFileId: string) {
          return [...this.base(dataFileId), 'state'] as const;
        },
        deferred(dataFileId: string) {
          return [...this.base(dataFileId), 'deferred'] as const;
        },
      },
      header_selection: {
        base: (dataFileId: string) =>
          [dataFileId, 'steps', 'header_selection'] as const,
      },
      headers_mapping: {
        base: (dataFileId: string) =>
          [dataFileId, 'steps', 'headers_mapping'] as const,
      },
      validation: {
        base(dataFileId: string) {
          return [dataFileId, 'steps', 'validation'];
        },
        totalErrorsCount(dataFileId: string) {
          return [...this.base(dataFileId), 'totalErrorsCount'];
        },
        validatedRows(
          dataFileId: string,
          searchParams?: ValidationSearchParams,
        ) {
          return [...this.base(dataFileId), 'validatedRows', searchParams];
        },
      },
      channel_assignment: {
        base(dataFileId: string) {
          return [dataFileId, 'steps', 'channel_assignment'] as const;
        },
        totalErrorsCount(dataFileId: string) {
          return [...this.base(dataFileId), 'totalErrorsCount'];
        },
        validatedRows(
          dataFileId: string,
          searchParams?: ValidationSearchParams,
        ) {
          return [...this.base(dataFileId), 'validatedRows', searchParams];
        },
      },
      success: {
        base: (dataFileId: string) => [dataFileId, 'success'] as const,
      },
    } as const,
    validationState(dataFileId: string) {
      return [dataFileId, 'validationState'] as const;
    },
  },
} as const;

export const QueryKeys = removeFalsyQueryKeys(
  queryKeysFabrics,
) as typeof queryKeysFabrics;

export const MutationKeys = {
  editDataTableField: 'editDataTableField',
  discardDataTableFields: 'discardDataTableFields',
};

// prettier-ignore
export type QueryKeyToResult<K extends readonly unknown[]> =
  K extends ReturnType<typeof QueryKeys.dataFile.steps.upload.state> ? AsyncUploadState | null : 
  K extends ReturnType<typeof QueryKeys.dataFile.steps.upload.deferred> ? DeferredPromise | null : 
  K extends ReturnType<typeof QueryKeys.dataFile.validationState> ? AsyncValidationState | null : 
  K extends ReturnType<typeof QueryKeys.template.collaborators> ? TemplateCollaborator[] :
  never;

type TypedQueryClient = Omit<QueryClient, 'getQueryData' | 'setQueryData'> & {
  getQueryData: <TTaggedQueryKey extends QueryKey = QueryKey>(
    queryKey: TTaggedQueryKey,
  ) => QueryKeyToResult<TTaggedQueryKey> | undefined;
  setQueryData: <
    TQueryFnData = unknown,
    TTaggedQueryKey extends QueryKey = QueryKey,
    TInferredQueryFnData = TTaggedQueryKey extends DataTag<
      unknown,
      infer TaggedValue
    >
      ? TaggedValue
      : TQueryFnData,
  >(
    queryKey: TTaggedQueryKey,
    updater: Updater<
      QueryKeyToResult<TTaggedQueryKey> | undefined,
      QueryKeyToResult<TTaggedQueryKey> | undefined
    >,
    options?: SetDataOptions,
  ) => TInferredQueryFnData | undefined;
};

export const useTypedQueryClient = () =>
  useQueryClient() as unknown as TypedQueryClient;
